import { ApolloClient, from, HttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import setAuth from '@app/action/set-auth'
import { getEndpoints } from '@app/const/config'
import store from '@app/store'

const host = window.location.hostname
const BASE_URL = getEndpoints(host).backend
const httpLink = new HttpLink({ uri: BASE_URL })

// the backend has two ways of saying the same thing: a rejected token, and a request that
// arrived with no token at all (which is what in-flight requests look like right after logout)
const AUTH_ERROR_MESSAGES = ['Context creation failed: Invalid token', 'Auth required']

const isAuthError = (e) => AUTH_ERROR_MESSAGES.includes(e.message)

const hasAuthError = ({ graphQLErrors, networkError }) => {
  if (graphQLErrors?.some(isAuthError)) return true
  // a non-200 response arrives as a networkError with the GraphQL errors nested inside
  return Boolean(networkError?.result?.errors?.some(isAuthError))
}

// several requests are usually in flight at once, so without this guard
// every one of them would fire its own redirect
let loggingOut = false

const invalidTokenLink = onError((error) => {
  if (loggingOut) return
  if (!store.getState().auth) return
  if (!hasAuthError(error)) return

  loggingOut = true
  console.log('Auth token no longer valid. Logging out.')
  // dispatching persists auth: null synchronously, and replace() has to follow in the
  // same tick - an invalid token can be part of the URL (/auth/:token), so anything
  // async in between lets admin-redirect remount and log in with it all over again.
  // that also rules out the log-out AC here: resetting the store is what triggers the
  // re-render that remounts admin-redirect, and the page load below wipes it anyway
  store.dispatch(setAuth(null))
  window.location.replace('/login')
})

const retryLink = new RetryLink({
  delay: {
    initial: 1000,
    max: 30000,
    jitter: false
  },
  attempts: {
    max: 2,
    retryIf (error, _operation) {
      const consoleReport =
        'Request failure detected. Details below.\n' +
        '-- operation name:' +
        _operation?.operationName +
        '\n' +
        '-- variables:\n' +
        JSON.stringify(_operation?.variables, null, 2) +
        '\n' +
        '-- query:\n' +
        _operation?.query?.loc?.source?.body +
        '\n' +
        '-- error:\n' +
        JSON.stringify(error, null, 2)
      console.log(consoleReport)

      // an auth failure the server has already returned will be returned again on every retry
      if (hasAuthError({ networkError: error })) return false

      // TODO no 4xx is worth retrying, not just invalid tokens. this would change
      // retry behaviour for every request in the app, so it needs its own ticket:
      // if (error?.statusCode >= 400 && error?.statusCode < 500) return false
      return !!error
    }
  }
})

const authLink = setContext((operation, { headers }) => {
  const { auth } = store.getState()
  return {
    uri: `${BASE_URL}${operation.operationName}`,
    headers: {
      ...headers,
      authorization: auth ? `Bearer ${auth}` : ''
    }
  }
})

export const client = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false
  }),
  link: from([invalidTokenLink, retryLink, authLink, httpLink]),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache', // we used to have 'network-only' here, but it caused some problems - pages displayed old data after being reloaded with F5
      errorPolicy: 'all'
    },
    mutate: {
      errorPolicy: 'all'
    }
  },
  connectToDevTools: true
})
